import api from '../utils/api';
import {
  fetchArticleInBrowser,
  extractArticleFromPageSource,
  clientFetchFailureReasons,
} from '../utils/articleFetch';

/**
 * News Service
 * All news generation and management API calls
 */

export const newsService = {
  /**
   * Generate news from URL.
   *
   * Some publishers' CDNs refuse the backend's datacenter IP outright. When
   * the server reports that, it sets `clientFetch` on the error and we retry
   * the fetch here in the browser - on the user's own connection, which those
   * CDNs do serve - then post the extracted article back.
   *
   * Both routes can still fail. When they do, the thrown error carries
   * `canPasteSource` and the id of the record the attempt was parked as, so the
   * caller can offer the manual page-source paste as a last resort - and have
   * it continue on that same record.
   *
   * @param {string} url
   * @param {string} [title]
   * @param {{ onClientFetchStart?: (reason: string) => void }} [hooks]
   */
  generateNews: async (url, title, { onClientFetchStart } = {}) => {
    try {
      return await api.post('/news/generate', { url, title });
    } catch (err) {
      const serverResponse = err.response?.data;

      if (!serverResponse?.clientFetch) {
        // A failure in the fetch stage parks the URL as a `failed` record and
        // reports its id; an AI or server fault does not. So that id is also
        // the signal that this attempt is one a pasted page source can rescue.
        if (serverResponse?.newsId) {
          err.failedNewsId = serverResponse.newsId;
          err.canPasteSource = true;
        }
        throw err;
      }

      onClientFetchStart?.(serverResponse.reason);

      // The record the server parked this failure as, so the browser's own
      // outcome lands on the same row
      const failedNewsId = serverResponse.newsId || null;

      let clientArticle;
      try {
        clientArticle = await fetchArticleInBrowser(url);
      } catch (browserErr) {
        if (failedNewsId) {
          // Best-effort: the user already has an error to read, so a failure
          // to file the audit trail must not replace it with a worse one
          try {
            await api.post(`/news/${failedNewsId}/fetch-error`, {
              reason: browserErr.reason,
              message: browserErr.message,
              statusCode: browserErr.status ?? null,
            });
          } catch (reportErr) {
            console.warn('[generateNews] could not record browser fetch error', reportErr);
          }
        }

        // Both routes are exhausted. The user gets one plain sentence - a CORS
        // rejection or an Akamai 403 is not something they can act on, and
        // naming either one just moves the confusion around. The specifics
        // stay on the error and in the console for whoever is debugging.
        console.warn('[generateNews] server and browser fetch both failed', {
          url,
          browserReason: browserErr.reason,
          browserError: browserErr.message,
          serverError: serverResponse.message,
        });

        const combined = new Error(
          browserErr.reason === clientFetchFailureReasons.mixedContent
            // The one case the user can actually fix, so keep the instruction
            ? browserErr.message
            : 'Unable to read news from this url'
        );
        combined.clientFetchReason = browserErr.reason;
        combined.serverMessage = serverResponse.message;
        // Nothing can fetch this page for us, but the user can still open it
        // themselves and paste what they see in View page source
        combined.canPasteSource = true;
        combined.failedNewsId = failedNewsId;
        throw combined;
      }

      return api.post('/news/generate', { url, title, clientArticle, failedNewsId });
    }
  },

  /**
   * Generate news from page source the user pasted in by hand.
   *
   * The last resort after both fetch routes failed. Extraction runs here in the
   * browser, exactly as it does for a browser-fetched page, so the server gets
   * the same validated article shape instead of a few hundred KB of raw markup.
   *
   * `failedNewsId` is what makes this a continuation rather than a new attempt:
   * the backend takes over that record, so the short ends up on the row the
   * original failure was parked as, carrying its fetch errors.
   *
   * Throws synchronously-shaped errors from the extractor (a plain Error with
   * `reason`) before any request is made.
   */
  generateNewsFromPageSource: async (url, pageSource, failedNewsId = null) => {
    const clientArticle = extractArticleFromPageSource(pageSource, url);

    return api.post('/news/generate', {
      url,
      clientArticle,
      failedNewsId,
      clientArticleSource: 'paste',
    });
  },

  /**
   * Get all news
   */
  getAllNews: async (params = {}) => {
    return api.get('/news', { params });
  },

  /**
   * Get news by ID
   */
  getNewsById: async (id) => {
    return api.get(`/news/${id}`);
  },

  /**
   * Get just the Inshorts hashId for a news id.
   *
   * Cheap on purpose - a freshly generated short polls this once a second
   * until its hash lands, which is what lets it go live.
   */
  getNewsHashId: async (id) => {
    return api.get(`/news/${id}/hash-id`);
  },

  /**
   * Get user's news
   */
  getMyNews: async (params = {}) => {
    return api.get('/news/user/my-news', { params });
  },

  /**
   * Edit news with history tracking
   */
  editNewsWithHistory: async (id, data) => {
    return api.post(`/news/${id}/edit`, data);
  },

  /**
   * Delete news
   */
  deleteNews: async (id) => {
    return api.delete(`/news/${id}`);
  },

  /**
   * Update news status (admin only)
   */
  updateNewsStatus: async (id, status) => {
    return api.patch(`/news/${id}/status`, { status });
  },

  /**
   * Get news statistics
   */
  getNewsStats: async () => {
    return api.get('/news/stats/overview');
  },

  /**
   * Get categories
   */
  getCategories: async () => {
    return api.get('/news/categories');
  },

  /**
   * Create category (admin only)
   */
  createCategory: async (data) => {
    return api.post('/news/categories', data);
  },

  /**
   * Unarchive news (restore from archived collection)
   */
  unarchiveNews: async (id) => {
    return api.post(`/news/${id}/unarchive`);
  },

  /**
   * Get archived news
   */
  getArchivedNews: async (params = {}) => {
    return api.get('/news/archived', { params });
  },

  /**
   * Get all archived news (admin only)
   */
  getAllArchivedNews: async (params = {}) => {
    return api.get('/news/archived/all', { params });
  },

  /**
   * Get all decks containing this news
   */
  getNewsDecks: async (newsId) => {
    return api.get(`/news/${newsId}/decks`);
  },

  /**
   * Hand an image URL to the media service and get a CDN URL back.
   *
   * The media service fetches the URL itself, so what matters is whether *it*
   * can reach the address - our own network access is irrelevant. Falls back to
   * our GCS bucket server-side, so a reachable URL always yields something
   * servable.
   */
  uploadImageFromUrl: async (imageUrl) => {
    return api.post('/news/media-upload', { imageUrl });
  },

  /**
   * Ask the backend for a signed GCS upload URL for one file/blob
   */
  getSignedUploadUrl: async ({ fileName, contentType, fileSize }) => {
    return api.post('/news/upload-signed-url', { fileName, contentType, fileSize });
  },

  /**
   * Hashtags available to attach, filtered by `q`.
   *
   * `language` only picks which readable title comes back - the labels
   * themselves are English on every tenant.
   */
  getHashtags: async ({ q, language, limit } = {}) => {
    return api.get('/news/hashtags', { params: { q, language, limit } });
  },

  /**
   * Inshorts' published category list, as `{ tag, label }`
   */
  getInshortsCategories: async (language) => {
    return api.get('/news/inshorts-categories', { params: { language } });
  },

  /**
   * Go live with news
   */
  goLiveNews: async (newsId, data) => {
    return api.post(`/news/${newsId}/go-live`, data);
  },

  /**
   * Unpublish a live news (take it down from the feed)
   */
  unpublishNews: async (newsId) => {
    return api.post(`/news/${newsId}/unpublish`);
  },
};
